import { computed } from '@vue/composition-api'

import store from '@/store'
import axios from 'axios'

const requestFiles = computed({
  get: () => store.state.uploading.requestFiles,
  set: val => {
    store.commit('uploading/SET_REQUEST_FILES', val)
  },
})

async function uploadParts(result, file, payload) {
  const FILE_CHUNK_SIZE = 50000000 // 50MB in size
  const promises = []
  const progress = []
  const pn = Math.ceil(file.size / FILE_CHUNK_SIZE)
  file.uploading = true
  for (let i = 0; i < pn; i++) {
    if (
      !requestFiles.value[payload.index]
      || !requestFiles.value[payload.index].data.isUploading
    ) {
      break
    }
    // eslint-disable-next-line no-await-in-loop
    const { data } = await store.dispatch('objects/addObject', {
      name: payload.bucketName,
      object: {
        bucketName: payload.bucketName,
        isFolder: false,
        key: `${
          payload.uploadPath ? `${payload.uploadPath}${file.name}` : file.name
        }`,
        isPublic: payload.isPublic,
        contentType: file.type,
        uploadId: result,
        partNumber: i + 1,
      },
      projectId: payload.projectId,
    })
    const signedUrl = data.result
    const start = i * FILE_CHUNK_SIZE
    const end = (i + 1) * FILE_CHUNK_SIZE
    const blob = i < pn ? file.file.slice(start, end) : file.file.slice(start)
    const options = {
      headers: {
        PartSize: blob.size,
        PartNumber: String(i + 1),
        'Access-Control-Allow-Origin': '*',
        'Content-Type': file.type,
        'x-amz-acl': payload.isPublic ? 'public-read' : 'private',
      },
      onUploadProgress: progressEvent => {
        progress[i] = progressEvent.loaded
        file.progress = progress.reduce((partialSum, a) => partialSum + a, 0)
      },
      signal: payload.controller.signal,
    }
    // eslint-disable-next-line no-await-in-loop
    promises.push(await axios.put(signedUrl, blob, options))
  }

  const resParts = await Promise.all(promises)
  const parts = resParts.map((part, index) => ({
    // eslint-disable-next-line no-useless-escape
    eTag: part.headers.etag ? part.headers.etag?.split('"').join('') : null,
    partNumber: index + 1,
  }))

  return {
    parts,
  }
}

function uploadFile(file, payload) {
  return new Promise((resolve, reject) => {
    store
      .dispatch('multiparts/initiateMultipart', {
        bucketName: payload.bucketName,
        key: `${
          payload.uploadPath ? `${payload.uploadPath}${file.name}` : file.name
        }`,
        private: !payload.isPublic,
        projectId: payload.projectId,
      })
      .then(response => {
        const { result } = response.data

        const temp = {
          bucketName: payload.bucketName,
          projectId: payload.projectId,
          id: result,
          object: {
            key: `${
              payload.uploadPath
                ? `${payload.uploadPath}${file.name}`
                : file.name
            }`,
            abort: false,
          },
        }

        uploadParts(result, file, payload)
          .then(() => {
            store
              .dispatch('multiparts/completeMultipartUpload', temp)
              .then(() => {
                resolve()
              })
              .catch(() => {
                reject()
              })
          })
          .catch(() => {
            temp.object.abort = true
            store.dispatch('multiparts/completeMultipartUpload', temp)
            reject()
          })
      })
  })
}

function fileToObject(file, payload) {
  return {
    bucketName: payload.bucketName,
    isFolder: false,
    key: `${
      payload.uploadPath ? `${payload.uploadPath}${file.name}` : file.name
    }`,
    uploadPath: payload.uploadPath,
    size: file.size,
    lastModified: new Date().toString(),
    manuallyAdded: true,
  }
}

export function uploadAllFiles(index) {
  const payload = requestFiles.value[index]
  const requests = payload.uploadData.files.map(file => uploadFile(file, payload.data))

  Promise.all(requests)
    .then(() => {
      store.dispatch(
        'objects/prependObjects',
        payload.uploadData.files.map(file => fileToObject(file, payload.data)),
      )
    })
    .catch(() => {})
    .finally(() => {
      requestFiles.value[index].data.controller = null
      requestFiles.value[index].uploadData.files = []
      requestFiles.value[index].data.isUploading = false
    })
}

export function cancelAllTokens(index) {
  requestFiles.value[index].data.controller.abort()
  requestFiles.value[index].data.controller = null
  requestFiles.value[index].uploadData.files = []
  requestFiles.value[index].data.isUploading = false
}
